// deps
import { useContext, useMemo } from "react";

// internal-deps
import { INetBankingPaymentGroup, NetBankingSpecificActions } from "@swiggy-private/payment-ui";

// interfaces
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import { useNavigate, CheckoutRoute, AnalyticsContext } from "@swiggy-private/checkout-core";
import { usePaymentActions } from "./UsePaymentActions";

// contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

// analytics
import { onMoreNetbankingClickEvent } from "@payment-client/analytics/events/Payment";

interface ReturnType {
    paymentActions: INetBankingPaymentGroup["paymentActions"];
    netBankingSpecificActions: NetBankingSpecificActions;
}

export const useNetBankingHandler = (props: PaymentGroupHandlerProps): ReturnType => {
    const { paymentGroup, onPay, onSelect } = props;
    const navigateTo = useNavigate();
    const { clickEvent } = useContext(AnalyticsContext);
    const { billTotal } = useContext(PaymentPayContext);
    const actions = usePaymentActions({ onPay, onSelect });

    const moreBanksActions: INetBankingPaymentGroup["paymentActions"]["moreBanksActions"] = useMemo(
        () => ({
            onClick: (): void => {
                onMoreNetbankingClickEvent(billTotal, clickEvent);
                navigateTo(CheckoutRoute.MORE_BANKS, { paymentGroup });
            },
        }),
        [navigateTo, paymentGroup, billTotal, clickEvent],
    );

    const netBankingSpecificActions = useMemo(
        (): NetBankingSpecificActions => ({
            moreBanksActions,
        }),
        [moreBanksActions],
    );

    const paymentActions = useMemo(
        (): INetBankingPaymentGroup["paymentActions"] => ({
            ...actions,
            ...netBankingSpecificActions,
        }),
        [actions, netBankingSpecificActions],
    );

    return { paymentActions, netBankingSpecificActions };
};
